import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, makeStyles, createStyles, Box, Typography } from '@material-ui/core';
import { AppColors } from '@lib/common';
import { AuditLogs } from './audit-logs';

interface StylesProps {
  hasTitle: boolean;
}

const useStyles = makeStyles(theme =>
  createStyles({
    buttonGroup: {
      marginLeft: 'auto',
    },
    selectedButton: {
      color: AppColors.DarkBlue,
      backgroundColor: AppColors.LightBlue,
      '&:hover': {
        color: AppColors.DarkBlue,
        backgroundColor: AppColors.LightBlue,
      },
    },
    sectionHeader: ({ hasTitle }: StylesProps) => ({
      display: 'flex',
      position: 'sticky',
      top: 0,
      zIndex: 100,

      padding: theme.spacing(1),
      alignItems: 'center',

      color: theme.palette.common.white,
      backgroundColor: hasTitle ? AppColors.DarkBlue : undefined,
      fontWeight: 'bold',
      fontSize: '200%',
    }),
    sectionContainer: {
      marginBottom: theme.spacing(1),
    },
    sectionBody: {
      marginTop: theme.spacing(1),
      padding: `${theme.spacing(1)}px 0`,
    },
  })
);

interface AuditHistoryTab {
  title: string;
}

interface HistorySectionProps {
  title?: string;
  tabs: HistorySectionTab[];
}

interface HistorySectionTab extends AuditHistoryTab {
  scope: string;
  key?: string;
}

export const AuditHistoryTabs: React.FC<HistorySectionProps> = ({ title, tabs }) => {
  const styles = useStyles({ hasTitle: !!title });

  const activeTabs = useMemo(() => tabs.filter(tab => tab.key), [tabs]);

  const [selectedTab, setSelectedTab] = useState(0);

  const setTab = useCallback(
    (index: number) => {
      if (index < activeTabs.length) setSelectedTab(index);
    },
    [activeTabs]
  );

  useEffect(() => {
    if (selectedTab >= activeTabs.length) setSelectedTab(0);
  }, [selectedTab, activeTabs.length]);

  return (
    <Box className={styles.sectionContainer}>
      <Box className={styles.sectionHeader}>
        <Typography variant="h5">{title}</Typography>
        {/* Using tabs because we can have multiple tabs that may be shown eventually, even if not currently active. */}
        {tabs.length > 1 && (
          <ButtonGroup
            color="secondary"
            aria-label="outlined primary button group"
            className={styles.buttonGroup}
          >
            {activeTabs.map((tab, tabIndex) => (
              <Button
                key={tab.title}
                onClick={() => setTab(tabIndex)}
                className={tabIndex === selectedTab ? styles.selectedButton : undefined}
              >
                {tab.title}
              </Button>
            ))}
          </ButtonGroup>
        )}
      </Box>
      <Box className={styles.sectionBody}>
        {activeTabs.length > 0 && (
          <AuditLogs scope={activeTabs[selectedTab].scope} id={activeTabs[selectedTab].key} />
        )}
      </Box>
    </Box>
  );
};
